import { Title } from '@solidjs/meta'
import { type Component, createResource, createSignal, For, onCleanup, Show } from 'solid-js'
import Button from '~/components/Button'
import ChatActivity from '~/components/ChatActivity'
import Chip from '~/components/Chip'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import Markdown from '~/components/Markdown'
import Select from '~/components/Select'
import { useToast } from '~/components/useToast'
import VoiceCallPanel from '~/layers/VoiceCallPanel'
import { useNavigate } from '~/router'
import { aiService, type ChatMessageType } from '~/services/ai.service'
import { authStore } from '~/stores/auth'
import { type Attachment, buildMessageWithDocuments, readAttachment } from '~/utils/attachments'
import type { ActivityType } from '~/utils/chat'
import { toolLabel } from '~/utils/chat'
import { resolveError } from '~/utils/error'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002/api'

/** WS de voz autenticado: token en la query porque un WS no lleva cabeceras. */
function voiceWsUrl(agentId: number) {
  const base = API_URL.replace(/^http/, 'ws')
  return `${base}/ai/voice?token=${encodeURIComponent(authStore.token ?? '')}&agentId=${agentId}`
}

interface BubbleType {
  role: 'user' | 'assistant'
  content: string
  /** Herramientas que usó para responder, para dar trazabilidad. */
  tools?: string[]
}

const ChatPage: Component = () => {
  const toast = useToast()
  const navigate = useNavigate()

  const [agents] = createResource(async () => {
    try {
      return await aiService.listAgents()
    } catch {
      return []
    }
  })
  const [providers] = createResource(async () => {
    try {
      return await aiService.listProviders()
    } catch {
      return []
    }
  })

  const [messages, setMessages] = createSignal<BubbleType[]>([])
  const [input, setInput] = createSignal('')
  const [sending, setSending] = createSignal(false)
  const [agentId, setAgentId] = createSignal<string>('')
  const [activity, setActivity] = createSignal<ActivityType>({ state: 'idle' })
  const [attachments, setAttachments] = createSignal<Attachment[]>([])
  const [reading, setReading] = createSignal(false)

  let controller: AbortController | undefined
  let scrollBox: HTMLDivElement | undefined
  let fileInput: HTMLInputElement | undefined

  onCleanup(() => controller?.abort())

  const hasProvider = () => Boolean(providers()?.some(p => p.category === 'chat'))

  /** Sólo los agentes usables en el panel: los de la web no salen aquí. */
  const internalAgents = () => (agents() ?? []).filter(a => a.scope !== 'public' && a.status === 1)

  const agentOptions = () => {
    const list = internalAgents()
    const def = list.find(a => a.isDefault)
    return [
      { value: '', title: def ? `${def.avatar ?? '🤖'} ${def.name} (por defecto)` : 'Sin agente' },
      ...list.map(a => ({
        value: String(a.id),
        title: `${a.avatar ?? '🤖'} ${a.name}`,
        subtitle: a.description ?? undefined
      }))
    ]
  }

  /** El agente activo: el elegido, o el marcado por defecto. */
  const activeAgent = () => {
    const list = internalAgents()
    return agentId() ? list.find(a => String(a.id) === agentId()) : list.find(a => a.isDefault)
  }

  const isVoiceAgent = () => activeAgent()?.type === 'voice'

  const scrollToBottom = () =>
    requestAnimationFrame(() => {
      if (scrollBox) scrollBox.scrollTop = scrollBox.scrollHeight
    })

  /** Lee los archivos elegidos y los añade como adjuntos, avisando de los no admitidos. */
  const onPickFiles = async (files: FileList | null) => {
    if (!files?.length) return
    setReading(true)
    try {
      for (const file of Array.from(files)) {
        const att = await readAttachment(file)
        if (att.kind === 'unsupported') {
          toast.addToast({ title: `${att.name}: ${att.reason}`, type: 'danger' })
          continue
        }
        if (att.kind === 'image' && !activeAgent()?.acceptsImages) {
          toast.addToast({ title: `${att.name}: este agente no acepta imágenes. Márcalo como modelo de visión en su configuración.`, type: 'danger' })
          continue
        }
        setAttachments(prev => [...prev, att])
      }
    } finally {
      setReading(false)
      if (fileInput) fileInput.value = ''
    }
  }

  const removeAttachment = (index: number) => setAttachments(prev => prev.filter((_, i) => i !== index))

  const send = async () => {
    const text = input().trim()
    const docs = attachments()
    if ((!text && !docs.length) || sending()) return

    // Documentos → su texto se antepone al mensaje que ve el modelo. Imágenes →
    // van aparte como data URLs (sólo si el agente es de visión).
    const sentContent = buildMessageWithDocuments(text || 'Analiza el documento adjunto.', docs)
    const images = docs.filter(d => d.kind === 'image' && d.dataUrl).map(d => d.dataUrl as string)

    // En la burbuja se muestra tu texto y los nombres de archivo, no el volcado
    // completo del documento.
    const shown = [text, docs.length ? `📎 ${docs.map(d => d.name).join(', ')}` : ''].filter(Boolean).join('\n\n')

    const history: ChatMessageType[] = [...messages().map(m => ({ role: m.role, content: m.content })), { role: 'user' as const, content: sentContent }]

    setMessages(prev => [...prev, { role: 'user', content: shown }, { role: 'assistant', content: '' }])
    setInput('')
    setAttachments([])
    setSending(true)
    // Pensando hasta que llegue el primer token o la primera tool.
    setActivity({ state: 'thinking' })
    scrollToBottom()

    controller = new AbortController()

    const patchLast = (fn: (b: BubbleType) => BubbleType) => {
      setMessages(prev => {
        const next = [...prev]
        next[next.length - 1] = fn(next[next.length - 1])
        return next
      })
      scrollToBottom()
    }

    try {
      await aiService.chatStream(
        history,
        event => {
          if (event.kind === 'content') {
            setActivity({ state: 'idle' })
            patchLast(b => ({ ...b, content: b.content + event.text }))
          } else if (event.kind === 'tool_call') {
            setActivity({ state: 'tool', name: event.tool })
            patchLast(b => ({ ...b, tools: [...(b.tools ?? []), event.tool] }))
          } else if (event.kind === 'tool_result') {
            // Vuelve a pensar: el modelo aún tiene que redactar con el resultado.
            setActivity({ state: 'thinking' })
          }
        },
        {
          agentId: agentId() ? Number(agentId()) : undefined,
          ...(images.length ? { images } : {}),
          signal: controller.signal
        }
      )
    } catch (err: unknown) {
      if ((err as Error)?.name === 'AbortError') {
        // Cancelado por el usuario: dejamos lo que se haya recibido.
      } else {
        // Quita la burbuja vacía para no dejar un hueco mudo.
        setMessages(prev => (prev[prev.length - 1]?.content === '' ? prev.slice(0, -1) : prev))
        toast.addToast({ title: resolveError(err), type: 'danger' })
      }
    } finally {
      setSending(false)
      setActivity({ state: 'idle' })
      controller = undefined
    }
  }

  const onKeyDown = (e: KeyboardEvent) => {
    // Enter envía; Shift+Enter hace salto de línea.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void send()
    }
  }

  const greeting = () => activeAgent()?.description || '¿En qué te ayudo hoy?'

  /** Sugerencias para arrancar: al pulsarlas se envían directamente. */
  const SUGGESTIONS = ['¿Qué servicios ofrecemos?', 'Resume nuestras tecnologías', 'Ayúdame a redactar una propuesta', '¿Qué hora es?']
  const useSuggestion = (text: string) => {
    if (sending()) return
    setInput(text)
    void send()
  }

  /**
   * El compositor: la tarjeta redondeada con el texto y el botón de enviar dentro.
   * `hero` la hace más alta para el estado inicial centrado.
   */
  const composer = (hero: boolean) => (
    <div class="rounded-3xl border border-border-strong bg-surface-muted/40 px-4 pt-2 pb-2 shadow-sm transition-colors focus-within:border-primary-500">
      {/* textarea propio y no el componente Textarea: ese es un campo de
          formulario con etiqueta flotante que pinta una franja para tapar el
          texto al hacer scroll, y sobre este fondo se veía como una banda rara.
          Aquí hace falta un compositor con placeholder normal, sin esa franja.
          Sin `disabled` al enviar: deshabilitarlo desenfocaba al dar Enter. */}
      <textarea
        class="w-full resize-none bg-transparent text-sm text-foreground placeholder:text-foreground-subtle focus:outline-none"
        rows={hero ? 3 : 2}
        placeholder="Escribe tu mensaje…"
        value={input()}
        onInput={e => setInput(e.currentTarget.value)}
        onKeyDown={onKeyDown}
      />

      {/* Archivos adjuntos ya leídos, con opción de quitarlos. */}
      <Show when={attachments().length}>
        <div class="flex flex-wrap gap-1.5 py-1">
          <For each={attachments()}>
            {(att, i) => (
              <Chip
                variant="soft"
                color="default"
                icon={att.kind === 'image' ? 'material-symbols-light:image-outline' : 'material-symbols-light:description-outline'}
                removable
                onRemove={() => removeAttachment(i())}
              >
                {att.name}
              </Chip>
            )}
          </For>
        </div>
      </Show>

      <div class="flex items-center justify-between gap-2 pt-1">
        <div class="flex items-center gap-2 min-w-0">
          {/* input de archivo oculto: no hay componente propio para elegir ficheros. */}
          <input ref={el => (fileInput = el)} type="file" multiple class="hidden" onChange={e => void onPickFiles(e.currentTarget.files)} />
          <Button
            variant="text"
            color="default"
            icon={reading() ? 'svg-spinners:180-ring-with-bg' : 'material-symbols-light:attach-file'}
            iconSize={20}
            disabled={reading()}
            onClick={() => fileInput?.click()}
            aria-label="Adjuntar archivo"
            tooltip="Adjuntar documento o imagen"
          />
          <div class="flex items-center gap-1.5 min-w-0 text-xs text-foreground-subtle">
            <span>{activeAgent()?.avatar ?? '🤖'}</span>
            <span class="truncate">{activeAgent()?.name ?? 'Asistente'}</span>
          </div>
        </div>
        <Button
          icon={sending() ? 'material-symbols-light:stop-outline' : 'material-symbols-light:arrow-upward'}
          color={sending() ? 'danger' : 'primary'}
          iconSize={20}
          pill
          class="shrink-0"
          disabled={!sending() && !input().trim() && !attachments().length}
          aria-label={sending() ? 'Detener' : 'Enviar'}
          onClick={() => (sending() ? controller?.abort() : void send())}
        />
      </div>
    </div>
  )

  return (
    <>
      <Title>Chat | Florez Jaramillo</Title>
      <Container>
        <div class="flex flex-col h-[calc(100dvh-8rem)]">
          {/* Barra superior mínima: elegir agente y limpiar. */}
          <div class="flex items-center justify-end gap-2 pb-3">
            <div class="w-52">
              <Select options={agentOptions()} value={agentId()} onChange={v => setAgentId(String(v ?? ''))} placeholder="Agente" />
            </div>
            <Show when={messages().length > 0}>
              <Button variant="soft" color="default" onClick={() => setMessages([])} disabled={sending()} tooltip="Nueva conversación" icon="material-symbols-light:edit-square-outline" />
            </Show>
          </div>

          {/* Un agente de voz no usa el chat de texto ni el proveedor de chat:
              habla por su propia conexión de audio (Gemini Live). */}
          <Show when={isVoiceAgent() ? activeAgent() : undefined} keyed>
            {agent => <VoiceCallPanel url={voiceWsUrl(agent.id)} name={agent.name} avatar={agent.avatar} />}
          </Show>

          <Show when={!isVoiceAgent()}>
            <Show when={providers.loading}>
              <div class="text-center py-8 text-foreground-subtle">Cargando...</div>
            </Show>

            <Show when={!providers.loading && !hasProvider()}>
              <div class="flex-1 flex flex-col items-center justify-center text-center gap-3">
                <Icon icon="material-symbols-light:warning-outline" size={40} class="text-warning" />
                <p class="text-foreground-muted max-w-md">No hay un proveedor de chat configurado para esta empresa. Configúralo para poder conversar.</p>
                <Button onClick={() => navigate('/admin/configuracion/ia')} prependIcon="material-symbols-light:settings-outline">
                  Configurar IA
                </Button>
              </div>
            </Show>

            <Show when={!providers.loading && hasProvider()}>
              {/* Estado inicial: compositor centrado tipo hero, con sugerencias. */}
              <Show when={messages().length === 0}>
                <div class="flex-1 flex flex-col items-center justify-center gap-6 px-2">
                  <div class="text-center space-y-3">
                    <Chip variant="soft" color="default" icon="material-symbols-light:star-shine">
                      {activeAgent()?.name ?? 'Asistente interno'}
                    </Chip>
                    <h1 class="text-2xl sm:text-3xl font-semibold text-foreground max-w-xl">{greeting()}</h1>
                  </div>
                  <div class="w-full max-w-2xl">{composer(true)}</div>
                  <div class="flex flex-wrap justify-center gap-2">
                    <For each={SUGGESTIONS}>
                      {s => (
                        <Chip variant="outlined" color="default" class="cursor-pointer select-none" onClick={() => useSuggestion(s)}>
                          {s}
                        </Chip>
                      )}
                    </For>
                  </div>
                </div>
              </Show>

              {/* Conversación en curso. */}
              <Show when={messages().length > 0}>
                <div ref={scrollBox} class="flex-1 overflow-y-auto space-y-4 pr-1">
                  <For each={messages()}>
                    {(msg, i) => (
                      <div class={msg.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                        <div
                          class={
                            msg.role === 'user'
                              ? 'max-w-[75%] rounded-2xl rounded-br-sm bg-primary-600 text-white px-4 py-2 whitespace-pre-wrap break-words'
                              : 'max-w-[75%] min-w-0 rounded-2xl rounded-bl-sm bg-surface-muted text-foreground px-4 py-2 break-words'
                          }
                        >
                          <Show when={msg.tools?.length}>
                            <div class="text-xs text-foreground-subtle mb-1 flex items-center gap-1">
                              <Icon icon="material-symbols-light:widgets-outline" size={12} />
                              {[...new Set(msg.tools)].map(toolLabel).join(' · ')}
                            </div>
                          </Show>

                          <div class="flex items-start gap-1">
                            <Show when={msg.role === 'assistant' && activeAgent()?.avatar}>
                              <span class="shrink-0">{activeAgent()?.avatar}</span>
                            </Show>

                            <div class="min-w-0 flex-1">
                              <Show
                                when={msg.content}
                                fallback={
                                  // Última burbuja vacía = está trabajando; si no, se quedó sin texto.
                                  <Show when={i() === messages().length - 1 && activity().state !== 'idle'} fallback={<span class="opacity-60">Sin respuesta</span>}>
                                    <ChatActivity activity={activity()} />
                                  </Show>
                                }
                              >
                                {/* Lo del usuario es su propio texto: se muestra tal cual.
                                  Sólo la respuesta del modelo viene en markdown. */}
                                <Show when={msg.role === 'assistant'} fallback={msg.content}>
                                  <Markdown content={msg.content} />
                                </Show>
                              </Show>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </For>

                  {/* Ya hay texto pero sigue trabajando (p.ej. una segunda tool). */}
                  <Show when={activity().state !== 'idle' && messages()[messages().length - 1]?.content}>
                    <div class="flex justify-start pl-1">
                      <ChatActivity activity={activity()} />
                    </div>
                  </Show>
                </div>

                <div class="pt-3">{composer(false)}</div>
              </Show>
            </Show>
          </Show>
        </div>
      </Container>
    </>
  )
}

export default ChatPage
